/**
 * Translated text in a reading illustration can be longer than the English the
 * art was drawn for. Shrinks every SVG <text> that runs past its box (the
 * smallest <rect> around its anchor, else the viewBox): first the font size,
 * down to 55%, then the glyph spacing via textLength. Idempotent per node.
 */
const MIN_SCALE = 0.55;

function anchorX(el: SVGTextElement): number | null {
  const xs = el.x?.baseVal;
  return xs && xs.numberOfItems > 0 ? xs.getItem(0).value : null;
}

export function fitSvgText(root: Element | null): void {
  if (!root) return;
  root.querySelectorAll("svg").forEach((svg) => {
    const vb = svg.viewBox?.baseVal;
    if (!vb || !vb.width) return;
    const rects: DOMRect[] = [];
    svg.querySelectorAll("rect").forEach((r) => {
      try {
        const b = (r as SVGRectElement).getBBox();
        if (b.width > 0 && b.height > 0) rects.push(new DOMRect(b.x, b.y, b.width, b.height));
      } catch {
        /* not rendered */
      }
    });
    svg.querySelectorAll("text").forEach((node) => {
      const el = node as SVGTextElement;
      if (!el.textContent || !el.textContent.trim()) return;
      const orig = el.getAttribute("data-fit-size");
      if (orig !== null) {
        el.style.fontSize = orig;
        el.removeAttribute("textLength");
        el.removeAttribute("lengthAdjust");
      }
      let box: DOMRect;
      try {
        box = el.getBBox();
      } catch {
        return;
      }
      const x = anchorX(el);
      if (!box.width || x == null) return;
      const anchor = getComputedStyle(el).textAnchor || el.getAttribute("text-anchor") || "start";
      const cy = box.y + box.height / 2;
      let left = vb.x + 4;
      let right = vb.x + vb.width - 4;
      let best = Infinity;
      for (const r of rects) {
        if (cy < r.y || cy > r.y + r.height || x < r.x || x > r.x + r.width) continue;
        const area = r.width * r.height;
        // a rect that only covers the text's own row is its chip / button
        if (area < best && r.width < vb.width) {
          best = area;
          left = r.x + 6;
          right = r.x + r.width - 6;
        }
      }
      const room =
        anchor === "middle" ? 2 * Math.min(x - left, right - x) : anchor === "end" ? x - left : right - x;
      if (room <= 0 || box.width <= room) return;
      const size = parseFloat(getComputedStyle(el).fontSize) || 12;
      if (orig === null) el.setAttribute("data-fit-size", el.style.fontSize || "");
      const scale = Math.max(MIN_SCALE, room / box.width);
      el.style.fontSize = `${(size * scale).toFixed(2)}px`;
      if (room / box.width < MIN_SCALE) {
        el.setAttribute("textLength", room.toFixed(1));
        el.setAttribute("lengthAdjust", "spacingAndGlyphs");
      }
    });
  });
}
